// liveClock — the app's ONE reading of "who is on the clock right now".
//
// GET /api/dispatch/now, its query key, its permission mirror, and the two
// derivations screens make from it. Two screens read it and they are in
// different sections (dispatch/screens/Now.js, office/records/Jobs.js), so
// this cannot live in either — sectionsDoNotCrossImport.test.js refuses that,
// correctly.
//
// ── Why the Jobs list reads THIS endpoint and not the classic one ──────────
//
// docs/PARITY_2026-09-08.md §17: the Office jobs list lost the live "running
// now" pill the classic screen put on every row
// (`git show d25258b^:client/src/components/Jobs/ActiveTimerBadge.js`, fed by
// GET /api/time-entries/active/all).
//
// That endpoint still exists and is deliberately unused —
// apps/shared/endpointCoverage.test.js records it as "covered by GET
// /api/dispatch/now → clocked_in", and the two really do read the same rows
// (`WHERE te.end_time IS NULL`). Reviving it here would put a SECOND reading
// of the same question in the client, and the two would answer differently:
//
//   · /dispatch/now joins `u.is_active = 1`; /active/all does not, so it lists
//     a running entry belonging to a deactivated account
//   · /dispatch/now returns `started_at` as a normalised ISO string and
//     `elapsed_seconds` computed against the SERVER's clock. /active/all
//     returns the raw SQLite `start_time` — "2026-09-09 13:00:00", no zone —
//     which `new Date()` reads as LOCAL. That is the exact bug the classic
//     badge carried a paragraph about, and the pill read "-1:-1:-1" for as
//     long as a phone's clock was behind the droplet's.
//
// So the capability comes back and the endpoint record stays true.
//
// ── The gate is mirrored, not guessed ─────────────────────────────────────
//
// server/routes/dispatch.js `canDispatch` admits an admin, or `timesheets`
// holding approve / manage_team / manage_all. A screen that asks a different
// question than the door either hides the pill from somebody the server would
// have answered, or polls a request that can only ever 403 — every thirty
// seconds, into somebody's console.
//
// Two useCan questions cover all four cases, and asking them is the whole
// mirror:
//
//   'approve'      asked directly
//   'manage_team'  asked directly — a MARKER, not a wildcard, so a promoted
//                  team lead who holds it passes the server's door and must
//                  pass this one
//   'manage_all'   useCan treats it as a wildcard on any resource
//   an admin       useCan answers true before it reads either argument
//
// Which is why this is not `user.role === 'admin' || …`: a role comparison in
// client code ignores per-user extra_permissions — the grant an admin makes on
// the Admin screen would change nothing — and
// server/utils/__tests__/roleCheckRatchet.test.js refuses one, by name, in
// exactly this situation. OfficeJobs.test.js reads canDispatch out of the route
// file AND useCan out of the hook, and fails if the composition stops covering
// the door.

import { get } from './http';

/** The endpoint, and the door in front of it, written down once. */
export const LIVE_CLOCK_ENDPOINT = {
  method: 'GET',
  path: '/dispatch/now',
  resource: 'timesheets',
  /** Every marker canDispatch admits. */
  markers: ['approve', 'manage_team', 'manage_all'],
  /** The two a screen has to ASK useCan; the other two it answers itself. */
  asks: ['approve', 'manage_team'],
  adminAlways: true,
};

/** Shared by every caller, so two screens never poll the same answer twice. */
export const LIVE_CLOCK_KEY = ['dispatch', 'now'];

/**
 * The response, stamped with when it arrived.
 *
 * `received_at` is what makes the elapsed clock immune to a wrong device
 * clock: the server says how long the shift has run, and the browser only adds
 * the time since the answer landed.
 */
export async function fetchLiveClock() {
  const data = await get(LIVE_CLOCK_ENDPOINT.path);
  return { ...data, received_at: Date.now() };
}

/**
 * May this person read it?
 *
 * Takes the ANSWERS to the asks above — `{ approve, manage_team }`, each from
 * a useCan call at the top of a component — rather than the user, so that the
 * only thing in the app deciding what a permission means stays
 * hooks/usePermission.js. Every ask has to be asked unconditionally: hooks may
 * not be conditional, and `useCan(…) || useCan(…)` short-circuits the second
 * one, which changes the hook order between renders.
 */
export function mayReadLiveClock(answers) {
  const a = answers || {};
  return LIVE_CLOCK_ENDPOINT.asks.some((ask) => !!a[ask]);
}

/**
 * Elapsed seconds for a row, ticking locally between answers.
 *
 * Moved here from dispatch/lib/format.js on 2026-09-09 (and re-exported from
 * there, so every dispatch caller and dispatchFormat.test.js are untouched):
 * it is a reading of THIS response's two fields, and the Jobs list needs the
 * same one.
 */
export function elapsedNow(elapsedSeconds, receivedAt, now = Date.now()) {
  const base = Math.max(0, Number(elapsedSeconds) || 0);
  const delta = Number.isFinite(receivedAt) ? Math.max(0, (now - receivedAt) / 1000) : 0;
  return Math.floor(base + delta);
}

/**
 * The running shifts on each job: job id (as a string) → the entries on it,
 * earliest first, which is the order the server returns them in.
 *
 * Keyed as strings on purpose. `job_id` arrives as a number and a caller holds
 * `job.id` — also a number today, and a string the moment one of them comes
 * off a URL or a form. A Map keyed by mixed types misses silently, and the
 * symptom is a pill that is simply absent.
 */
export function runningByJob(live) {
  const byJob = new Map();
  for (const entry of (live && live.clocked_in) || []) {
    if (!entry || entry.job_id === null || entry.job_id === undefined) continue;
    const key = String(entry.job_id);
    if (!byJob.has(key)) byJob.set(key, []);
    byJob.get(key).push(entry);
  }
  return byJob;
}

/**
 * Who is on a job, as a sentence — the lead namer of the crew currently
 * clocked in, and how many others.
 *
 * Same shape as office/records/Jobs.js `crewSummary` ("Dave +2"), because a
 * row that says "Dave +2 crew" for the roster and something else for who is
 * actually on site is a row a person has to decode.
 */
export function runningWho(entries) {
  const names = (entries || []).map((e) => e && e.name).filter(Boolean);
  if (!names.length) return '';
  if (names.length === 1) return names[0];
  return `${names[0]} +${names.length - 1}`;
}
